import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { auditFetch, buildAuditHeaders } from '../utils/auditFetch';
import '../styles/Dashboard.css';

const fileCategories = [
  ['laboratorio', 'Laboratorio'],
  ['imagenologia', 'Imagenologia'],
  ['receta_previa', 'Receta previa'],
  ['informe', 'Informe medico'],
  ['otro', 'Otro'],
];

const formatFileSize = (bytes = 0) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getCategoryLabel = (category) => (
  fileCategories.find(([value]) => value === category)?.[1] || 'Archivo clinico'
);

const ClinicalFilesPanel = ({
  appointmentId,
  patientId,
  patientName,
  user,
  canUpload = true,
  compact = false,
  refreshSignal = 0,
}) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [category, setCategory] = useState('laboratorio');
  const [notes, setNotes] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (appointmentId) params.set('appointmentId', appointmentId);
    if (patientId) params.set('patientId', patientId);
    return params.toString();
  }, [appointmentId, patientId]);

  const loadFiles = useCallback(() => {
    if (!queryString) return;
    setIsLoading(true);
    auditFetch(`${backendUrl}/api/clinical-files?${queryString}`, {}, user)
      .then((res) => (res.ok ? res.json() : { files: [] }))
      .then((data) => setFiles(Array.isArray(data.files) ? data.files : []))
      .catch((error) => {
        console.error('Error loading clinical files:', error);
        setStatusMessage('No se pudieron cargar los archivos clinicos.');
      })
      .finally(() => setIsLoading(false));
  }, [backendUrl, queryString, user]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles, refreshSignal]);

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!selectedFile || !appointmentId || !patientId || isUploading) return;

    const formData = new FormData();
    formData.append('appointmentId', appointmentId);
    formData.append('patientId', patientId);
    formData.append('patientName', patientName || '');
    formData.append('category', category);
    formData.append('notes', notes);
    formData.append('file', selectedFile);

    setIsUploading(true);
    setStatusMessage('');

    try {
      const response = await auditFetch(`${backendUrl}/api/clinical-files`, {
        method: 'POST',
        body: formData,
      }, user);

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo subir el archivo');

      setFiles((currentFiles) => [data.file, ...currentFiles]);
      setSelectedFile(null);
      setNotes('');
      event.target.reset();
      setStatusMessage(user.role === 'patient'
        ? 'Archivo clinico cargado correctamente. El medico fue notificado en la consulta.'
        : 'Archivo clinico cargado correctamente.');
    } catch (error) {
      console.error('Error uploading clinical file:', error);
      setStatusMessage(error.message || 'No se pudo subir el archivo clinico.');
    } finally {
      setIsUploading(false);
    }
  };

  const openFile = (fileRecord) => {
    const url = `${backendUrl}${fileRecord.downloadUrl}`;
    const headers = buildAuditHeaders(user);
    fetch(url, { headers })
      .then((res) => {
        if (!res.ok) throw new Error('No se pudo abrir el archivo');
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      })
      .catch((error) => {
        console.error('Error opening clinical file:', error);
        setStatusMessage('No se pudo abrir el archivo clinico.');
      });
  };

  return (
    <div className={`clinical-files-panel ${compact ? 'clinical-files-panel-compact' : ''}`}>
      <div className="clinical-files-header">
        <div>
          <h3>Archivos clinicos</h3>
          <p>PDF, imagenes y examenes asociados a esta teleconsulta.</p>
        </div>
        <span>{files.length} archivo(s)</span>
      </div>

      {canUpload && (
        <form className="clinical-files-upload" onSubmit={handleUpload}>
          <div className="clinical-files-upload-grid">
            <label>
              <span>Tipo</span>
              <select value={category} onChange={(event) => setCategory(event.target.value)}>
                {fileCategories.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <label className="clinical-file-input">
              <span>Archivo</span>
              <input
                type="file"
                accept=".pdf,image/png,image/jpeg,image/webp,image/gif"
                onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
              />
            </label>
          </div>
          <label>
            <span>Nota clinica opcional</span>
            <input
              type="text"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Ej. Hemograma, RX torax, informe externo"
              maxLength={250}
            />
          </label>
          <button type="submit" className="btn btn-primary full-width" disabled={!selectedFile || isUploading}>
            {isUploading ? 'Subiendo...' : 'Subir archivo clinico'}
          </button>
        </form>
      )}

      {statusMessage && <p className="clinical-files-message">{statusMessage}</p>}

      <div className="clinical-files-list">
        {isLoading ? (
          <p className="clinical-files-empty">Cargando archivos...</p>
        ) : files.length === 0 ? (
          <p className="clinical-files-empty">No hay archivos clinicos cargados.</p>
        ) : (
          files.map((fileRecord) => (
            <div key={fileRecord.id} className="clinical-file-item">
              <div className="clinical-file-icon">
                {fileRecord.mimeType === 'application/pdf' ? 'PDF' : 'IMG'}
              </div>
              <div className="clinical-file-data">
                <strong>{fileRecord.originalName}</strong>
                <span>
                  {getCategoryLabel(fileRecord.category)} · {formatFileSize(fileRecord.fileSize)}
                </span>
                {fileRecord.notes && <p>{fileRecord.notes}</p>}
                <small>
                  Subido por {fileRecord.uploadedByName || fileRecord.uploadedByRole || 'usuario'} ·{' '}
                  {new Date(fileRecord.createdAt).toLocaleString('es-EC')}
                </small>
              </div>
              <button type="button" onClick={() => openFile(fileRecord)}>
                Revisar
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ClinicalFilesPanel;
